import { auth } from "../firebase-config";

/**
 * Cloud Functions base URL
 * Update this with your actual Cloud Functions URL from Firebase Console
 * Format: https://region-projectId.cloudfunctions.net
 */

const PROCESS_AUDIO_URL = process.env.REACT_APP_PROCESS_AUDIO_URL;
const GET_NOTES_URL = process.env.REACT_APP_GET_NOTES_URL;
const DELETE_NOTE_URL = process.env.REACT_APP_DELETE_NOTE_URL;
const GET_USAGE_LIMITS_URL = process.env.REACT_APP_GET_USAGE_LIMITS_URL;

if (!PROCESS_AUDIO_URL || !GET_NOTES_URL || !DELETE_NOTE_URL || !GET_USAGE_LIMITS_URL) {
    console.warn("One or more Cloud Function URLs are not set in .env.local.");
}

/**
 * Get the current user's ID token for authentication
 * @returns {Promise<string>} The ID token
 */
async function getIdToken() {
    const user = auth.currentUser;
    if (!user) {
        throw new Error("User not authenticated");
    }
    return await user.getIdToken();
}

/**
 * Process audio: transcribe and generate clinical notes
 * @param {Blob} audioBlob - The audio file blob
 * @param {string} patientId - The patient ID
 * @param {string} fileName - Optional file name
 * @param {Function} onProgress - Optional callback for progress updates
 * @returns {Promise<Object>} Processing result with transcription and notes
 */
export async function processAudio(audioBlob, patientId, fileName, onProgress) {
    if (!PROCESS_AUDIO_URL) {
        throw new Error("Process Audio Function URL not configured");
    }
    try {
        onProgress?.(0, "Preparing audio...");
        // Convert blob to base64
        const reader = new FileReader();
        const base64Promise = new Promise((resolve, reject) => {
            reader.onload = () => {
                const base64 = reader.result.split(",")[1]; // Remove data:audio/wav;base64, prefix
                resolve(base64);
            };
            reader.onerror = reject;
        });
        reader.readAsDataURL(audioBlob);
        const audioData = await base64Promise;
        onProgress?.(10, "Authenticating...");
        const idToken = await getIdToken();
        onProgress?.(20, "Uploading audio...");
        const response = await fetch(PROCESS_AUDIO_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({
                audioData: audioData,
                patientId: patientId,
                mimeType: audioBlob.type || "audio/wav",
                fileName: fileName || "audio",
            }),
        });
        onProgress?.(90, "Processing complete...");
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        onProgress?.(100, "Done!");
        return result.data;
    } catch (error) {
        console.error("Error processing audio:", error);
        throw error;
    }
}

/**
 * Retrieve all notes for a patient
 * @param {string} patientId - The patient ID
 * @returns {Promise<Array>} Array of note objects
 */
export async function getNotes(patientId) {
    if (!GET_NOTES_URL) {
        throw new Error("Get Notes Function URL not configured");
    }
    try {
        const idToken = await getIdToken();
        const response = await fetch(
            `${GET_NOTES_URL}?patientId=${encodeURIComponent(patientId)}`,
            {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${idToken}`,
                },
            }
        );
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        return result.data;
    } catch (error) {
        console.error("Error fetching notes:", error);
        throw error;
    }
}

export async function getUsageLimits() {
    if (!GET_USAGE_LIMITS_URL) {
        throw new Error("Get Usage Limits Function URL not configured");
    }
    try {
        const idToken = await getIdToken();
        const response = await fetch(GET_USAGE_LIMITS_URL, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${idToken}`,
            },
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error("Error fetching usage limits:", error);
        throw error;
    }
}


/**
 * Delete a specific note
 * @param {string} patientId - The patient ID
 * @param {string} noteId - The note ID
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteNote(patientId, noteId) {
    if (!DELETE_NOTE_URL) {
        throw new Error("Delete Note Function URL not configured");
    }
    try {
        const idToken = await getIdToken();
        const response = await fetch(DELETE_NOTE_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({
                patientId: patientId,
                noteId: noteId,
            }),
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        return result;
    } catch (error) {
        console.error("Error deleting note:", error);
        throw error;
    }
}


/**
 * Convert audio blob to WAV format (helper function)
 * @param {Blob} audioBlob - The audio blob
 * @returns {Promise<Blob>} WAV formatted blob
 */
export async function convertToWAV(audioBlob) {
    // If already WAV, return as is
    if (audioBlob.type === "audio/wav") {
        return audioBlob;
    }

    // For other formats, you might need additional processing
    // For now, return the blob as is and let Whisper handle format detection
    return audioBlob;
}
